import React from "react";
import tw from "tailwind-styled-components";
import { useNavigate } from "react-router-dom";
import { format2Decimals, getDeltaUI, getPercentageChange } from "../../helpers";
import Tooltip from "../Tooltip";

function OperationalInsightsRow({ impactSummary, complaintSummary, setIsShowComplaintAnalytics }) {
  const navigate = useNavigate();

  return (
    // <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
    //   <BusinessImpactSummaryCard data={impactSummary} />
    //   <ComplaintSummaryCard data={complaintSummary} />
    // </div>
    <div className="mt-6">
      <div className="flex gap-4 overflow-x-auto sm:grid sm:grid-cols-1 lg:grid-cols-2 sm:gap-6">
        <div className="min-w-[85%] sm:min-w-0">
          <BusinessImpactSummaryCard data={impactSummary} navigate={navigate} />
        </div>
        <div className="min-w-[85%] sm:min-w-0">
          <ComplaintSummaryCard data={complaintSummary} setIsShowComplaintAnalytics={setIsShowComplaintAnalytics}/>
        </div>
      </div>
    </div>

  );
}

const BusinessImpactSummaryCard = ({ data, navigate }) => {

  const totalMoveInChange = getPercentageChange(
    data?.curMonth?.moveIn?.count || 0,
    data?.lastMonth?.moveIn?.count || 0
  );

  const totalMoveInDelta = getDeltaUI(totalMoveInChange);

  const totalMoveOutChange = getPercentageChange(
    data?.curMonth?.moveOut?.count || 0,
    data?.lastMonth?.moveOut?.count || 0
  );

  const totalMoveOutDelta = getDeltaUI(totalMoveOutChange, "negative");

  const getOccupancyColor = (value) => {
    if (value >= 85) return "text-green-600";
    if (value >= 65) return "text-amber-500";
    return "text-red-600";
  };

  return (
    <Card>
      <div>
        <Header>
          <Title>Business Impact Summary</Title>
          <Arrow clickable={false} />
        </Header>

        <SubTitle>
          Current Month · Compared to previous month
        </SubTitle>

        <MetricsRow>
          <Metric>
            <MetricValueRow>
              <MetricValue className="text-green-600">
                ₹{data?.curMonth?.moveIn?.totalRent}
              </MetricValue>
              <MetricMeta className='cursor-pointer' onClick={() => navigate('/tenants/active')}>(+{data?.curMonth?.moveIn?.count})</MetricMeta>
            </MetricValueRow>

            {/* <MetricLabel>New Tenant Revenue</MetricLabel> */}
            {/* Context row */}
            {/* <div className="text-xs text-gray-500">
              +6 tenants
            </div> */}

            <MetricLabelWithTooltip
              label="New Tenant Rev"
              tooltip={`₹${data?.curMonth?.moveIn?.totalRent} indicates the revenue increment from newly added tenants. (${data?.curMonth?.moveIn?.count}) represents the number of new tenants added this month. ${totalMoveInDelta.text} shows the change in new tenant count compared to last month.`}
            />
            <MetricDelta className={totalMoveInDelta.className}>{totalMoveInDelta.text}</MetricDelta>
          </Metric>

          <Metric>
            {/* <MetricValue className="text-red-600">₹12,500</MetricValue> */}
            <MetricValueRow>
              <MetricValue className={
                data?.nextMonth?.net > 0
                  ? "text-green-600"
                  : data?.nextMonth?.net < 0
                    ? "text-red-600"
                    : "text-gray-500"
              }>
                ₹{data?.nextMonth?.net ?? 0}
              </MetricValue>
              <MetricMeta>(-{data?.curMonth?.moveOut?.count})</MetricMeta>
            </MetricValueRow>
            {/* <MetricLabel>Projected Revenue Loss</MetricLabel> */}
            <MetricLabelWithTooltip
              label="Revenue Impact"
              tooltip={`Estimated revenue impact for next month based on current month move-outs and expected move-ins.
              (-${data?.curMonth?.moveOut?.count}) indicates the number of move-outs in the current month.
                ${totalMoveOutDelta.text} shows the increase/decrease in move-outs compared to last month.`}
            />
            <MetricDelta className={totalMoveOutDelta.className}>{totalMoveOutDelta.text}</MetricDelta>
          </Metric>

          <Metric className="hidden lg:flex">
            <MetricValueRow>
              <MetricValue className={getOccupancyColor(data?.curMonth?.occupancyPercent)}>{data?.curMonth?.occupancyPercent}%</MetricValue>
              <MetricMeta className='font-semibold'>(₹{data?.vacantInventoryValue || 0})</MetricMeta>
            </MetricValueRow>
            <MetricLabelWithTooltip
              label="Occupancy Rate"
              tooltip={`Current occupancy percentage for this month.
              ₹${Number(
                data?.vacantInventoryValue || 0
              ).toLocaleString()} represents the estimated value of unoccupied inventory(vacant beds).`}
            />

            <MetricDelta className="text-gray-600">{format2Decimals(data?.lastMonth?.occupancyPercent)}%</MetricDelta>
          </Metric>
        </MetricsRow>
      </div>
    </Card>
  );
};

const ComplaintSummaryCard = ({ data, setIsShowComplaintAnalytics }) => {
  const totalChange = getPercentageChange(
    Number(data?.total) || 0,
    Number(data?.lastMonthTotal) || 0
  );

  const pendingChange = getPercentageChange(
    Number(data?.pending) || 0,
    Number(data?.lastMonthPending) || 0
  );

  const resolutionRate = data?.total
    ? Math.round((data?.resolved / data?.total) * 100)
    : 0;

  const lastMonthResolutionRate = data?.lastMonthTotal
    ? Math.round((data?.lastMonthResolved / data?.lastMonthTotal) * 100)
    : 0;

  const resolutionRateChange = getPercentageChange(
    resolutionRate,
    lastMonthResolutionRate
  );

  const totalDelta = getDeltaUI(totalChange, "negative");
  const pendingDelta = getDeltaUI(pendingChange, "negative");
  const resolutionDelta = getDeltaUI(resolutionRateChange);

  const getResolutionColor = (value) => {
    if (value >= 85) return "text-green-600";
    if (value >= 65) return "text-amber-500";
    return "text-red-600";
  };
  //const resolutionRate = getResolutionRate(data);
  return (
    <Card>
      <div>
        <Header>
          <Title>Complaint Summary</Title>
          <Arrow clickable={true} onClick={() => {
            setIsShowComplaintAnalytics(true);
          }} />
        </Header>

        <SubTitle>
          Current Month · Compared to previous month
        </SubTitle>

        <MetricsRow>
          <Metric>
            <MetricValue>{data?.total}</MetricValue>
            <MetricLabelWithTooltip
              label="Total Complaints"
              tooltip="Change in total complaints raised compared to last month"
            />
            <MetricDelta className={totalDelta.className}>
              {totalDelta.text}
            </MetricDelta>
          </Metric>

          <Metric>
            <MetricValue className="text-amber-500">{data?.pending}</MetricValue>
            <MetricLabelWithTooltip
              label="Open"
              tooltip="Change in pending complaints compared to last month"
            />
            <MetricDelta className={pendingDelta.className}>
              {pendingDelta.text}
            </MetricDelta>
          </Metric>

          <Metric>
            <MetricValue className={getResolutionColor(resolutionRate)}>{resolutionRate}%</MetricValue>
            <MetricLabelWithTooltip
              label="Resolution Rate"
              tooltip="Change in complaint resolution percentage compared to last month"
            />
            <MetricDelta className={resolutionDelta.className}>
              {resolutionDelta.text}
            </MetricDelta>
          </Metric>
        </MetricsRow>
      </div>
    </Card>
  );
};

const MetricLabelWithTooltip = ({ label, tooltip }) => (
  <div className="flex gap-1">
    <span className="text-sm text-gray-500">{label}</span>
    <Tooltip label={tooltip}>
      <span className="cursor-help text-gray-400 text-xs">ⓘ</span>
    </Tooltip>
  </div>
);

/* ---------- styles ---------- */

const Card = tw.div`
  bg-white rounded-xl shadow-sm px-6 py-5
  flex flex-col justify-between
`;

const MetricsRow = tw.div`
  grid grid-cols-1
  sm:grid-cols-2
  xl:grid-cols-3
  gap-4
  mt-6
`;

const Header = tw.div`
  flex items-center justify-between
`;

// const Title = tw.h3`
//   text-base font-semibold text-gray-800
// `;

const Title = tw.h3`
  text-sm font-semibold text-gray-700 uppercase tracking-wide
`;

const SubTitle = tw.p`
  text-[11px] sm:text-xs
  text-gray-400
  mt-0.5
`;


const Metric = tw.div`
  flex flex-col gap-1
  min-w-0
`;

const MetricValueRow = tw.div`
  flex items-baseline gap-2
`;

const MetricMeta = tw.span`
  text-xs text-gray-500
`;

const MetricValue = tw.div`
  text-xl sm:text-2xl
  font-semibold
  text-gray-900
`;


const MetricLabel = tw.div`
  text-sm text-gray-500
`;

const MetricDelta = tw.div`
  text-sm font-medium
`;

const Arrow = ({ clickable, ...props }) => (
  <span
    {...props}
    className={`text-gray-400 text-lg ${clickable ? "cursor-pointer" : ""}`}
  >
    →
  </span>
);


export default OperationalInsightsRow;
